/**
 * Series builders for the Categories/Accounts entity cards: per-month spend,
 * the budget that applied to each month, and month-end balance history walked
 * backwards from the live balance. (The cards' forward forecast comes from
 * utils/analysisForecast, the Analysis page's engine.)
 *
 * Month keys use the app-wide `Mon-YY` form (see useBudgetData), built from
 * the transaction date's UTC month so both sides always agree.
 */

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const RANGE_OPTIONS = [
  { value: '3M', label: '3M', months: 3 },
  { value: '6M', label: '6M', months: 6 },
  { value: '1Y', label: '1Y', months: 12 },
];

export const FORECAST_OPTIONS = [
  { value: 'off', label: 'Off', months: 0 },
  { value: '1M', label: '1M', months: 1 },
  { value: '3M', label: '3M', months: 3 },
  { value: '6M', label: '6M', months: 6 },
];

export const monthsForRange = (range) =>
  (RANGE_OPTIONS.find((o) => o.value === range) || RANGE_OPTIONS[1]).months;

export const monthsForForecast = (forecast) =>
  (FORECAST_OPTIONS.find((o) => o.value === forecast) || FORECAST_OPTIONS[0]).months;

const monthKeyOf = (year, monthIndex) =>
  `${MONTH_NAMES[monthIndex]}-${String(year).slice(2)}`;

export const monthKeyOfDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return monthKeyOf(d.getUTCFullYear(), d.getUTCMonth());
};

/** Key of the month `today` falls in — local time, matching monthKeysForRange. */
export const currentMonthKey = (today = new Date()) =>
  monthKeyOf(today.getFullYear(), today.getMonth());

/**
 * Mean of a card's monthly values. With includePartialMonth=false the current
 * (still in-progress) month is dropped first, so a half-elapsed month doesn't
 * drag the average down; if the window contains nothing else, all points are
 * kept rather than dividing by zero.
 */
export const averageMonthlyValue = (points, { includePartialMonth = true, today = new Date() } = {}) => {
  const partialKey = currentMonthKey(today);
  const filtered = includePartialMonth ? points : points.filter((p) => p.month !== partialKey);
  const usable = filtered.length ? filtered : points;
  return usable.reduce((sum, p) => sum + p.value, 0) / (usable.length || 1);
};

/** The last `count` calendar months ending with the current one. */
export const monthKeysForRange = (count, today = new Date()) => {
  const keys = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    keys.push({
      key: monthKeyOf(d.getFullYear(), d.getMonth()),
      year: d.getFullYear(),
      monthIndex: d.getMonth(),
    });
  }
  return keys;
};

/** The next `count` calendar months after the current one. */
export const monthKeysAhead = (count, today = new Date()) => {
  const keys = [];
  for (let i = 1; i <= count; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    keys.push({
      key: monthKeyOf(d.getFullYear(), d.getMonth()),
      year: d.getFullYear(),
      monthIndex: d.getMonth(),
    });
  }
  return keys;
};

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * A category's per-month series over the window, from useBudgetData's maps.
 * Mixed categories chart income − spend, mirroring CategoryAnalysisDialog.
 * Returns { points: [{ month, value }], meaning: 'spend'|'income'|'net', hasActivity }.
 */
export const categorySpendSeries = (category, budgetData, monthKeys) => {
  const incomeSeries = (budgetData && budgetData.incomeCategories[category.id]) || {};
  const expenseSeries = (budgetData && budgetData.expenseCategories[category.id]) || {};
  const hasIncome = Object.values(incomeSeries).some((v) => v !== 0);
  const hasExpense = Object.values(expenseSeries).some((v) => v !== 0);
  const meaning = hasIncome && hasExpense ? 'net' : hasIncome ? 'income' : 'spend';

  const points = monthKeys.map(({ key }) => {
    const income = incomeSeries[key] || 0;
    const expense = expenseSeries[key] || 0; // stored positive
    const value = meaning === 'income' ? income : meaning === 'spend' ? expense : income - expense;
    return { month: key, value: round2(value) };
  });

  return { points, meaning, hasActivity: points.some((p) => p.value !== 0) };
};

/**
 * Monthly allocated amount for one budget in one month. Mirrors
 * computeMonthlyAmount in BudgetProgressCard/BudgetMatrixView so the card's
 * budget guide always matches what the Budgets pages report.
 */
export const computeMonthlyBudgetAmount = (budget, year, monthIndex) => {
  const start = new Date(budget.startDate);
  const end = budget.endDate ? new Date(budget.endDate) : new Date(2099, 11, 31);
  const monthStart = new Date(year, monthIndex, 1);
  const monthEnd = new Date(year, monthIndex + 1, 0);
  if (end < monthStart || start > monthEnd) return 0; // no overlap

  const msPerDay = 1000 * 60 * 60 * 24;
  const overlapStart = start > monthStart ? start : monthStart;
  const overlapEnd = end < monthEnd ? end : monthEnd;
  const overlapDays = Math.max(0, Math.round((overlapEnd - overlapStart) / msPerDay) + 1);
  const daysInMonth = Math.round((monthEnd - monthStart) / msPerDay) + 1;

  if (budget.isRecurring) {
    if (!budget.isProrated) {
      switch (budget.recurringPeriod) {
        case 'monthly':
          return Math.abs(budget.amount);
        case 'weekly': {
          const targetDow = start.getDay();
          let count = 0;
          for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
            if (d >= start && d <= end && d.getDay() === targetDow) count++;
          }
          return Math.abs(count * budget.amount);
        }
        case 'quarterly': {
          const startMonth = start.getMonth();
          const currentMonth = monthStart.getMonth();
          const diff = (monthStart.getFullYear() - start.getFullYear()) * 12 + (currentMonth - startMonth);
          if (diff >= 0 && diff % 3 === 0) return Math.abs(budget.amount);
          return 0;
        }
        case 'yearly':
          return start.getMonth() === monthStart.getMonth() ? Math.abs(budget.amount) : 0;
        default:
          return Math.abs(budget.amount);
      }
    }
    switch (budget.recurringPeriod) {
      case 'weekly':
        return Math.abs(budget.amount / 7) * overlapDays;
      case 'monthly':
        return Math.abs(budget.amount) * (overlapDays / daysInMonth);
      case 'quarterly':
        return Math.abs(budget.amount) * (overlapDays / 91.25);
      case 'yearly':
        return Math.abs(budget.amount) * (overlapDays / 365);
      default:
        return Math.abs(budget.amount) * (overlapDays / daysInMonth);
    }
  }
  if (overlapDays <= 0) return 0;
  return Math.abs(budget.amount) * (overlapDays / daysInMonth);
};

/**
 * The budget that applied to each month of the window for one category —
 * the card's stepped budget guide. Returns an array aligned with monthKeys,
 * or null when no budget covered any month of the window.
 */
export const categoryBudgetByMonth = (categoryId, budgets, monthKeys) => {
  const matching = (budgets || []).filter(
    (b) => b.categoryId === categoryId && b.isActive !== false
  );
  if (!matching.length) return null;

  const amounts = monthKeys.map(({ year, monthIndex }) =>
    round2(matching.reduce((sum, b) => sum + computeMonthlyBudgetAmount(b, year, monthIndex), 0))
  );
  return amounts.some((a) => a !== 0) ? amounts : null;
};

/**
 * Month-end balances for the window, walked backwards from the account's live
 * balance so no snapshot storage is needed: balance at the end of month M is
 * the live balance minus everything that happened after M.
 *
 * Returns { points: [{ month, value }], baseline } where `baseline` is the
 * balance at the start of the window (for the change-over-range delta), and
 * the last point is the live balance.
 */
export const accountBalanceHistory = (account, transactions, monthKeys) => {
  const sumsByMonth = {};
  (transactions || []).forEach((t) => {
    if (t.account !== account.id && t.account !== account.name) return;
    const key = monthKeyOfDate(t.date);
    sumsByMonth[key] = (sumsByMonth[key] || 0) + (t.amount || 0);
  });

  const values = new Array(monthKeys.length);
  values[monthKeys.length - 1] = account.balance || 0;
  for (let i = monthKeys.length - 2; i >= 0; i--) {
    values[i] = values[i + 1] - (sumsByMonth[monthKeys[i + 1].key] || 0);
  }
  const baseline = values[0] - (sumsByMonth[monthKeys[0].key] || 0);

  return {
    points: monthKeys.map(({ key }, i) => ({ month: key, value: round2(values[i]) })),
    baseline: round2(baseline),
  };
};
